import { makeAutoObservable, runInAction } from 'mobx';
import { apiClient, envUrls } from '../../shared/api/api-client';

/**
 * «Что нового» — лента изменений HKeeper.
 *
 * Источник один — файл в репозитории api-server, его же показывает
 * hkeeper.ru/changelog. Здесь добавляется только непрочитанное: дату
 * последнего просмотра храним в localStorage, бейдж в меню — число
 * записей новее неё. Отелю важно заметить, что что-то появилось, а не
 * синхронизировать прочитанное между устройствами, поэтому сервер
 * про это ничего не знает.
 */

export type ChangelogKind = 'new' | 'improved' | 'fixed';

export interface ChangelogEntry {
  date: string;
  title: string;
  text: string;
  module: string;
  module_label: string;
  kind: ChangelogKind;
  kind_label: string;
}

const SEEN_KEY = 'hk_changelog_seen_date';

export const KIND_COLORS: Record<ChangelogKind, string> = {
  new: 'green',
  improved: 'blue',
  fixed: 'volcano',
};

export class ChangelogStore {
  entries: ChangelogEntry[] = [];
  latestDate: string | null = null;
  isLoading = false;
  loaded = false;
  seenDate: string | null = null;

  constructor() {
    makeAutoObservable(this);
    try {
      this.seenDate = localStorage.getItem(SEEN_KEY);
    } catch {
      this.seenDate = null;
    }
  }

  /** Сколько записей новее последнего просмотра. */
  get unreadCount(): number {
    if (!this.seenDate) return this.entries.length;
    return this.entries.filter((e) => e.date > (this.seenDate as string)).length;
  }

  isUnread(entry: ChangelogEntry): boolean {
    return !this.seenDate || entry.date > this.seenDate;
  }

  async fetch() {
    if (this.isLoading) return;
    this.isLoading = true;
    try {
      const { data } = await apiClient.get(`${envUrls.api}/api/v1/changelog`);
      runInAction(() => {
        this.entries = data.entries ?? [];
        this.latestDate = data.latest_date ?? null;
        this.loaded = true;
      });
    } catch {
      // Лента — не критичный раздел: при сбое просто остаётся пустой.
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** Отметить всё прочитанным — вызывается при открытии страницы. */
  markSeen() {
    if (!this.latestDate) return;
    this.seenDate = this.latestDate;
    try {
      localStorage.setItem(SEEN_KEY, this.latestDate);
    } catch {
      // private mode — бейдж просто вернётся при следующем заходе
    }
  }
}

export const changelogStore = new ChangelogStore();
